import {useCallback} from 'react';
import assert from 'assert-ts';
import get from 'lodash/get';
import type {UseValueById} from 'schema/form/types';
import type {DataValue} from 'schemaDefinition/types';
import type {RootState} from 'store/types';
import {useAppSelector} from 'store/hooks';

const useValueSelector = (path: string | undefined, entityId: string) =>
  useCallback(
    (state: RootState): DataValue => {
      if (path === undefined) {
        return null;
      }
      const changeRequest = (
        state.metadataEdit.metadata?.changeRequests ?? []
      ).find(cr => cr.id.toString() === entityId);

      if (!changeRequest) {
        return null;
      }

      const [entity, ...localParts] = path.split('.');
      const localPath = localParts.join('.');

      switch (entity as 'work' | 'expression' | 'manifestation') {
        case 'work': {
          const work = state.metadataEdit.metadata?.work;
          return work
            ? localPath === ''
              ? work
              : (get(work, localPath) ?? null)
            : null;
        }
        case 'expression': {
          const expressionId = changeRequest.expression.id;
          const expression = state.metadataEdit.metadata?.expressions.find(
            e => e.id === expressionId,
          );
          return expression
            ? localPath === ''
              ? expression
              : (get(expression, localPath) ?? null)
            : null;
        }
        case 'manifestation': {
          const manifestationId = changeRequest.manifestation.id;
          const manifestation =
            state.metadataEdit.metadata?.manifestations.find(
              m => m.id === manifestationId,
            );
          return manifestation
            ? localPath === ''
              ? manifestation
              : (get(manifestation, localPath) ?? null)
            : null;
        }
        default: {
          assert(false, 'useChangeRequestOriginalValue: unknown entity', {
            path,
          });
          return null;
        }
      }
    },
    [entityId, path],
  );

export const useChangeRequestOriginalValue: UseValueById = (
  localPath: string | undefined,
  entityId: string,
): DataValue | undefined => {
  const selector = useValueSelector(localPath, entityId);
  const value = useAppSelector(selector);

  return value;
};
